import { useCallback } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type {
  BulkResolveUsersFieldChange,
  BulkResolveUsersRowResult,
  BulkResolveUsersRowStatus,
} from "@event-planner/shared";
import {
  userImportSpec,
  type ParsedUserImportRow,
  type RawUserImportRow,
} from "@event-planner/shared/imports";
import {
  ResolveAndImportDialog,
  type ProposalRow,
} from "../../components/imports/ResolveAndImportDialog";
import { type BulkImportResultBucket } from "../../components/BulkImportResultTable";
import { runWithConcurrency } from "../../utils/concurrency";
import {
  bulkResolveUsers,
  createContact,
  updateContact,
} from "../../services/userManagementService";

interface ExcelUserImportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Fires after a non-zero import run completes so the parent can refetch. */
  onImported?: () => void;
}

type UserProposal = ProposalRow<
  ParsedUserImportRow,
  BulkResolveUsersRowResult
>;

/**
 * Shape returned from `writeRows`. Aggregates the per-row create/update
 * results so the done-phase buckets can render. There's no
 * `bulkCreateUsers` callable — the dialog dispatches per-row to either
 * `createContact` (for 'new' rows) or `updateContact` (for matched rows
 * the organizer opted in to overwrite) via a concurrency runner.
 */
interface UserWriteResult {
  results: Array<{
    lineNumber: number;
    status: "added" | "updated" | "duplicate" | "failed";
    message: string | null;
    fieldsUpdated?: string[];
  }>;
  added: number;
  updated: number;
}

const MAX_CONCURRENCY = 5;

/**
 * Default include + locked policy.
 *
 * - `new` rows: pre-checked, ready to create.
 * - `matches_*` with non-empty `proposedChanges`: pre-checked, toggleable.
 *   Including means "overwrite the diffed fields on the existing record".
 * - `matches_*` with no diff: locked off (no-op overwrite).
 * - `failed` / `invalid`: locked off.
 */
export function defaultIncludeForUserResolution(
  resolution: BulkResolveUsersRowResult | null,
): { include: boolean; locked: boolean } {
  if (!resolution) return { include: false, locked: true };
  switch (resolution.status) {
    case "new":
      return { include: true, locked: false };
    case "matches_user":
    case "matches_contact": {
      const hasDiff = (resolution.proposal?.proposedChanges?.length ?? 0) > 0;
      return hasDiff
        ? { include: true, locked: false }
        : { include: false, locked: true };
    }
    case "failed":
      return { include: false, locked: true };
  }
}

/**
 * Excel-based bulk user import wrapped through the shared
 * `ResolveAndImportDialog` shell.
 *
 * Resolve phase calls `bulkResolveUsers`, surfacing ADR-057 identity
 * matches per row: existing user (silent dedup at write time), existing
 * contact (silent dedup), or new (createContact will create a fresh
 * row). Today's createContact callable returns `alreadyExists: true` on
 * collision; the dialog used to swallow that into a single "duplicate"
 * bucket. The resolve surface makes the prior-event affiliations of
 * matched people visible BEFORE the write, which is the load-bearing
 * piece of the architecture.
 *
 * Write phase still runs per-row createContact via runWithConcurrency
 * (there's no bulkCreateUsers callable; per-row + concurrency is the
 * existing path). Race-condition fallback: if a row was 'new' at resolve
 * time but `createContact.alreadyExists=true` at write time (because
 * someone created it in another tab between the two calls), the bucket
 * shows "duplicate" rather than failing the row.
 */
export function ExcelUserImportDialog({
  open,
  onClose,
  onImported,
}: ExcelUserImportDialogProps) {
  const resolveRows = useCallback(
    async (rows: ParsedUserImportRow[]) => {
      const response = await bulkResolveUsers({
        rows: rows.map((r) => ({
          email: r.email,
          role: r.role,
          firstName: r.firstName,
          lastName: r.lastName,
          honorificPrefix: r.honorificPrefix,
          honorificSuffix: r.honorificSuffix,
          title: r.title,
          organization: r.organization,
          phone: r.phone,
          workCity: r.workCity,
          workState: r.workState,
          bio: r.bio,
          linkedIn: r.linkedIn,
          website: r.website,
          photoURL: r.photoURL,
          dietaryRestrictions: r.dietaryRestrictions,
          accessibilityNeeds: r.accessibilityNeeds,
          internalNotes: r.internalNotes,
          internalTags: r.internalTags,
        })),
      });
      return response.results;
    },
    [],
  );

  const writeProposals = useCallback(
    async (proposals: UserProposal[]): Promise<UserWriteResult> => {
      const results: UserWriteResult["results"] = [];
      let added = 0;
      let updated = 0;
      const indexed = proposals.map((p, i) => ({ proposal: p, index: i }));
      await runWithConcurrency(
        indexed,
        MAX_CONCURRENCY,
        async ({ proposal, index }) => {
          const row = proposal.parsed.data;
          const resolution = proposal.resolution;
          const matchedPersonId =
            resolution?.proposal?.userId ?? resolution?.proposal?.contactId ?? null;
          const fieldsToOverwrite =
            resolution?.proposal?.proposedChanges?.map((c) => c.field) ?? [];
          try {
            if (matchedPersonId && fieldsToOverwrite.length > 0) {
              const result = await updateContact({
                personId: matchedPersonId,
                firstName: row.firstName,
                lastName: row.lastName,
                honorificPrefix: row.honorificPrefix,
                honorificSuffix: row.honorificSuffix,
                photoURL: row.photoURL,
                title: row.title,
                organization: row.organization,
                phone: row.phone,
                workCity: row.workCity,
                workState: row.workState,
                bio: row.bio,
                linkedIn: row.linkedIn,
                website: row.website,
                dietaryRestrictions: row.dietaryRestrictions,
                accessibilityNeeds: row.accessibilityNeeds,
                internalNotes: row.internalNotes,
                internalTags: row.internalTags,
                fieldsToOverwrite,
              });
              updated += 1;
              results[index] = {
                lineNumber: index,
                status: "updated",
                message:
                  result.fieldsUpdated.length > 0
                    ? `Updated: ${result.fieldsUpdated.join(", ")}`
                    : null,
                fieldsUpdated: result.fieldsUpdated,
              };
              return;
            }
            const result = await createContact({
              email: row.email,
              role: row.role,
              firstName: row.firstName,
              lastName: row.lastName,
              honorificPrefix: row.honorificPrefix,
              honorificSuffix: row.honorificSuffix,
              title: row.title,
              organization: row.organization,
              phone: row.phone,
              workCity: row.workCity,
              workState: row.workState,
              bio: row.bio,
              linkedIn: row.linkedIn,
              website: row.website,
              photoURL: row.photoURL,
              internalNotes: row.internalNotes,
              internalTags: row.internalTags,
              dietaryRestrictions: row.dietaryRestrictions,
              accessibilityNeeds: row.accessibilityNeeds,
              stagedAudienceIds: [],
            });
            const status = result.alreadyExists ? "duplicate" : "added";
            if (status === "added") added += 1;
            results[index] = {
              lineNumber: index,
              status,
              message: result.alreadyExists ? "Already exists" : null,
            };
          } catch (err) {
            results[index] = {
              lineNumber: index,
              status: "failed",
              message: err instanceof Error ? err.message : "Import failed",
            };
          }
        },
      );
      return { results, added, updated };
    },
    [],
  );

  const renderProposalCard = useCallback(
    (proposal: UserProposal, onToggle: () => void) => (
      <ProposalCard proposal={proposal} onToggle={onToggle} />
    ),
    [],
  );

  return (
    <ResolveAndImportDialog<
      ParsedUserImportRow,
      BulkResolveUsersRowResult,
      UserWriteResult
    >
      open={open}
      onClose={onClose}
      onImported={onImported}
      title="Import Users from Excel"
      entityNoun={{ singular: "User", plural: "Users" }}
      pickerHelpText={
        <>
          Upload an Excel file (.xlsx) with one row per user. Required:{" "}
          <strong>email</strong>. Role defaults to <em>invitee</em> when
          blank. Each email is resolved server-side against the global
          users + contacts collections; you'll see who each email matches
          (and their prior-event affiliations) before any contact is
          created.
        </>
      }
      testIdSlug="user"
      spec={normalizingUserSpec}
      templateFileName="user-import-template.xlsx"
      sheetName="Users"
      resolveRows={resolveRows}
      writeProposals={writeProposals}
      defaultIncludeForResolution={(resolution) =>
        defaultIncludeForUserResolution(resolution)
      }
      renderProposalCard={renderProposalCard}
      buildResultBuckets={({ writeResult, includedProposals, skippedProposals }) =>
        buildUserBuckets(writeResult, includedProposals, skippedProposals)
      }
      importedAnythingFromResult={(result) => result.added + result.updated > 0}
    />
  );
}

// ---------------------------------------------------------------------------
// Spec wrapper that runs the existing header-alias normalization before
// validate() — preserves the dialog's prior tolerance for casing /
// underscore variations on the Users sheet headers.
// ---------------------------------------------------------------------------

const normalizingUserSpec = {
  ...userImportSpec,
  validate: (raw: Record<string, string | undefined>, lineNumber: number) =>
    userImportSpec.validate(normalizeHeaders(raw) as RawUserImportRow, lineNumber),
};

function normalizeHeaders(
  raw: Record<string, string | undefined>,
): RawUserImportRow {
  const out: Record<string, string | undefined> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (value === undefined) continue;
    const k = key.toLowerCase().replace(/[\s_-]/g, "");
    if (k === "email" || k === "emailaddress") out.email = value;
    else if (k === "firstname") out.firstName = value;
    else if (k === "lastname") out.lastName = value;
    else if (k === "honorificprefix" || k === "prefix")
      out.honorificPrefix = value;
    else if (k === "honorificsuffix" || k === "suffix")
      out.honorificSuffix = value;
    else if (k === "role") out.role = value;
    else if (k === "title") out.title = value;
    else if (k === "organization" || k === "company" || k === "org")
      out.organization = value;
    else if (
      k === "phone" ||
      k === "phonenumber" ||
      k === "mobile" ||
      k === "mobilenumber" ||
      k === "mobilephone"
    )
      out.phone = value;
    else if (k === "workcity" || k === "city") out.workCity = value;
    else if (
      k === "workstate" ||
      k === "state" ||
      k === "stateorregion" ||
      k === "region"
    )
      out.workState = value;
    else if (
      k === "dietaryrestrictions" ||
      k === "dietaryrestriction" ||
      k === "dietaryrestrictionsorhotelspecialrequest" ||
      k === "dietaryrestrictionshotelspecialrequest"
    )
      out.dietaryRestrictions = value;
    else if (
      k === "accessibilityneeds" ||
      k === "accessibility" ||
      k === "mobilityneeds"
    )
      out.accessibilityNeeds = value;
    else if (
      k === "internalnotes" ||
      k === "notes" ||
      k === "hotelspecialrequest" ||
      k === "specialrequest"
    )
      out.internalNotes = value;
    else out[key] = value;
  }
  return out as RawUserImportRow;
}

// ---------------------------------------------------------------------------
// Per-row proposal card
// ---------------------------------------------------------------------------

function ProposalCard({
  proposal,
  onToggle,
}: {
  proposal: UserProposal;
  onToggle: () => void;
}) {
  const { resolution, parsed, include, includeLocked, lineNumber } = proposal;

  if (!resolution) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 1.5 }}
        data-testid={`proposal-row-${lineNumber}`}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox
            checked={false}
            disabled
            data-testid={`proposal-include-${lineNumber}`}
          />
          <Stack spacing={0.5} flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={500}>
              Row {lineNumber}: {parsed.data.email || <em>(missing email)</em>}
            </Typography>
            <Typography variant="caption" color="error.main">
              {parsed.message ?? "Format error"}
            </Typography>
          </Stack>
          <Chip size="small" label="Format error" color="error" />
        </Stack>
      </Paper>
    );
  }

  const tooltipParts: string[] = [resolution.email];
  if (resolution.proposal?.priorEventNames.length) {
    tooltipParts.push(
      `Prior events: ${resolution.proposal.priorEventNames.join(", ")}`,
    );
  }
  if (resolution.status === "matches_user") {
    tooltipParts.push("Already a registered user globally.");
  } else if (resolution.status === "matches_contact") {
    tooltipParts.push("Already a pre-claim contact globally.");
  }

  const display = resolution.proposal?.displayName ?? parsed.data.displayName;
  const proposedChanges = resolution.proposal?.proposedChanges ?? [];
  const hasDiff = proposedChanges.length > 0;
  const isMatch =
    resolution.status === "matches_user" ||
    resolution.status === "matches_contact";

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, opacity: includeLocked ? 0.85 : 1 }}
      data-testid={`proposal-row-${lineNumber}`}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Checkbox
          checked={include}
          disabled={includeLocked}
          onChange={onToggle}
          data-testid={`proposal-include-${lineNumber}`}
        />
        <Tooltip
          title={
            <Box sx={{ whiteSpace: "pre-line" }}>{tooltipParts.join("\n")}</Box>
          }
        >
          <Stack spacing={0.5} flex={1} minWidth={0}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              {resolution.proposal?.photoURL && (
                <Avatar
                  src={resolution.proposal.photoURL}
                  sx={{ width: 24, height: 24 }}
                />
              )}
              <Typography variant="body2" fontWeight={500}>
                Row {lineNumber}: {display ?? resolution.email}
              </Typography>
              <StatusChip status={resolution.status} hasDiff={hasDiff} />
              {isMatch && hasDiff && include && (
                <Chip
                  size="small"
                  label={`Overwrite ${proposedChanges.length} field${proposedChanges.length === 1 ? "" : "s"}`}
                  color="warning"
                  variant="outlined"
                />
              )}
              <Typography variant="caption" color="text.secondary">
                Role: {parsed.data.role}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {resolution.email}
            </Typography>
            {resolution.proposal?.priorEventNames.length ? (
              <Typography variant="caption" color="text.secondary">
                Prior events:{" "}
                {resolution.proposal.priorEventNames.join(", ")}
              </Typography>
            ) : null}
            {isMatch && hasDiff && (
              <DiffList changes={proposedChanges} />
            )}
          </Stack>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

function DiffList({ changes }: { changes: BulkResolveUsersFieldChange[] }) {
  return (
    <Box
      sx={{
        mt: 0.75,
        pl: 1,
        borderLeft: 2,
        borderColor: "warning.light",
      }}
      data-testid="proposal-diff"
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 500, display: "block", mb: 0.25 }}
      >
        Will overwrite:
      </Typography>
      <Stack spacing={0.25}>
        {changes.map((c) => (
          <Typography
            key={c.field}
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace", fontSize: 11 }}
          >
            <strong>{c.field}:</strong>{" "}
            <span style={{ textDecoration: "line-through" }}>
              {formatDiffValue(c.existing)}
            </span>{" "}
            → <strong>{formatDiffValue(c.incoming)}</strong>
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

function formatDiffValue(value: string | string[] | null): string {
  if (value == null) return "(empty)";
  if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : "(empty)";
  return value;
}

function StatusChip({
  status,
  hasDiff,
}: {
  status: BulkResolveUsersRowStatus;
  hasDiff?: boolean;
}) {
  switch (status) {
    case "new":
      return <Chip size="small" label="New, ready" color="success" />;
    case "matches_user":
      return (
        <Chip
          size="small"
          label={hasDiff ? "Match, fields differ" : "Match, no changes"}
          color={hasDiff ? "warning" : "default"}
        />
      );
    case "matches_contact":
      return (
        <Chip
          size="small"
          label={hasDiff ? "Contact, fields differ" : "Contact, no changes"}
          color={hasDiff ? "warning" : "default"}
        />
      );
    case "failed":
      return <Chip size="small" label="Failed" color="error" />;
  }
}

// ---------------------------------------------------------------------------
// Done-phase result bucketing
// ---------------------------------------------------------------------------

interface ResultRow {
  lineNumber: number;
  email: string;
  displayName: string | null;
  status: "success" | "updated" | "duplicate" | "failed" | "skipped" | "invalid";
  message: string | null;
}

function buildUserBuckets(
  writeResult: UserWriteResult | null,
  includedProposals: UserProposal[],
  skippedProposals: UserProposal[],
): BulkImportResultBucket[] {
  const writeRows: ResultRow[] = includedProposals.map((p, i) => {
    const r = writeResult?.results[i];
    if (!r) {
      return {
        lineNumber: p.lineNumber,
        email: p.parsed.data.email,
        displayName: p.parsed.data.displayName,
        status: "failed" as const,
        message: "Skipped at write phase",
      };
    }
    const status: ResultRow["status"] =
      r.status === "added"
        ? "success"
        : r.status === "updated"
          ? "updated"
          : r.status === "duplicate"
            ? "duplicate"
            : "failed";
    return {
      lineNumber: p.lineNumber,
      email: p.parsed.data.email,
      displayName: p.parsed.data.displayName,
      status,
      message: r.message ?? null,
    };
  });

  const skipRows: ResultRow[] = skippedProposals.map((p) => {
    if (!p.resolution) {
      return {
        lineNumber: p.lineNumber,
        email: p.parsed.data.email || "(no email)",
        displayName: p.parsed.data.displayName,
        status: "invalid" as const,
        message: p.parsed.message ?? "Format error",
      };
    }
    if (p.resolution.status === "matches_user") {
      return {
        lineNumber: p.lineNumber,
        email: p.resolution.email,
        displayName: p.resolution.proposal?.displayName ?? null,
        status: "duplicate" as const,
        message: "Already a registered user",
      };
    }
    if (p.resolution.status === "matches_contact") {
      return {
        lineNumber: p.lineNumber,
        email: p.resolution.email,
        displayName: p.resolution.proposal?.displayName ?? null,
        status: "duplicate" as const,
        message: "Already a contact",
      };
    }
    return {
      lineNumber: p.lineNumber,
      email: p.resolution.email,
      displayName: null,
      status: "skipped" as const,
      message: p.resolution.message ?? null,
    };
  });

  const all = [...writeRows, ...skipRows].sort(
    (a, b) => a.lineNumber - b.lineNumber,
  );

  const success = all.filter((r) => r.status === "success");
  const updated = all.filter((r) => r.status === "updated");
  const duplicate = all.filter((r) => r.status === "duplicate");
  const skipped = all.filter((r) => r.status === "skipped");
  const failed = all.filter(
    (r) => r.status === "failed" || r.status === "invalid",
  );

  return [
    {
      id: "success",
      label: "Successfully Added",
      color: "success",
      rows: success.map((r) => ({
        id: `s-${r.lineNumber}`,
        primary: r.displayName ?? r.email,
        secondary: r.email,
      })),
    },
    {
      id: "updated",
      label: "Updated",
      color: "info",
      description:
        "Existing person record overwritten with workbook values for the diffed fields.",
      rows: updated.map((r) => ({
        id: `u-${r.lineNumber}`,
        primary: r.displayName ?? r.email,
        secondary: r.message ? `${r.email} — ${r.message}` : r.email,
      })),
    },
    {
      id: "duplicate",
      label: "Already Exists",
      color: "warning",
      description:
        "Existing user or contact already on file. No new row was created.",
      rows: duplicate.map((r) => ({
        id: `d-${r.lineNumber}`,
        primary: r.displayName ?? r.email,
        secondary: r.message ? `${r.email} — ${r.message}` : r.email,
      })),
    },
    {
      id: "skipped",
      label: "Skipped",
      color: "default",
      rows: skipped.map((r) => ({
        id: `k-${r.lineNumber}`,
        primary: r.email,
        secondary: r.message ?? undefined,
      })),
    },
    {
      id: "failed",
      label: "Failed",
      color: "error",
      description: "Validation errors or callable rejections.",
      rows: failed.map((r) => ({
        id: `f-${r.lineNumber}`,
        primary: r.displayName ?? r.email,
        secondary: `Row ${r.lineNumber}: ${r.message ?? "unknown error"}`,
      })),
    },
  ];
}
